/**
 * ApiOfflineService
 *
 * Wrapper HTTP para chamadas à API no contexto offline/desktop.
 * Usa fetch nativo com o token armazenado localmente.
 *
 * Todas as chamadas são feitas com Bearer token lido do AppConfigRepository.
 */
import { getElectronAPI } from "@/plugins/desktop";
import { SYSTEM_VARIABLE } from "@/modules/Application";

function getBaseUrl() {
  return (SYSTEM_VARIABLE.BASE_URL_API || "").replace(/\/$/, "");
}

async function getToken() {
  const api = getElectronAPI();
  return api ? api.config.get("api_token") : null;
}

async function buildHeaders(extra = {}) {
  const token = await getToken();
  return {
    "Authorization": token ? `Bearer ${token}` : "",
    "Accept":        "application/json",
    "Content-Type":  "application/json",
    ...extra,
  };
}

async function parseResponseBody(resp) {
  const text = await resp.text();
  if (!text) return null;

  try {
    return JSON.parse(text);
  } catch (_) {
    return text;
  }
}

function getErrorMessage(data, fallback) {
  if (!data) return fallback;
  if (typeof data === "string") return data;
  if (data.errors) {
    const details = JSON.stringify(data.errors);
    return data.message ? `${data.message}: ${details}` : details;
  }
  if (data.message) return data.message;
  if (data.msg) return data.msg;
  if (data.error) return data.error;
  return fallback;
}

function buildHttpError(method, endpoint, resp, data) {
  const fallback = `${method} ${endpoint} falhou (HTTP ${resp.status})`;
  const error = new Error(`${fallback}: ${getErrorMessage(data, "erro sem corpo")}`);
  error.response = {
    status: resp.status,
    data,
  };
  return error;
}

const ApiOfflineService = {
  /**
   * GET /api/:endpoint
   * @param {string} endpoint - caminho relativo (ex.: "offline/produtos")
   * @param {object} params   - query string params
   */
  async get(endpoint, params = {}) {
    const baseUrl = getBaseUrl();
    const headers = await buildHeaders();
    const query   = new URLSearchParams(params).toString();
    const url     = `${baseUrl}/api/${endpoint}${query ? "?" + query : ""}`;

    const resp = await fetch(url, { headers });
    if (!resp.ok) {
      const body = await parseResponseBody(resp);
      throw buildHttpError("GET", endpoint, resp, body);
    }
    return resp.json();
  },

  /**
   * POST /api/:endpoint
   * @param {string} endpoint - caminho relativo
   * @param {object} payload  - corpo da requisição
   */
  async post(endpoint, payload = {}) {
    const baseUrl = getBaseUrl();
    const headers = await buildHeaders();
    const url     = `${baseUrl}/api/${endpoint}`;

    const resp = await fetch(url, {
      method:  "POST",
      headers,
      body:    JSON.stringify(payload),
    });
    if (!resp.ok) {
      const body = await parseResponseBody(resp);
      throw buildHttpError("POST", endpoint, resp, body);
    }
    return resp.json();
  },

  /**
   * Envia XML(s) NFC-e assinados + payload completo da venda para o backend
   * transmitir à SEFAZ. Após a autorização, o back_nuts usa o objeto
   * `venda` de cada entrada para gerar o financeiro e movimentar o estoque.
   *
   * POST /api/offline/nfce/transmitir-xml
   *
   * Cada item de `nfces` contém:
  *   - uuid_venda_terminal, chave, xml_assinado,
  *     id_terminal_venda, id_configuracao, id_configuracao_nfce,
  *     serie, numero, modelo, emitida_em
   *   - venda: {
   *       valor_total,           // igual ao vNF do XML
   *       desconto, acrescimo,
   *       troco,                 // somente quando Σ pagamentos > valor_total
   *       cliente: { id_cliente, cnpj_cpf, nome, nome_fantasia, email, telefone, ie } | null,
   *       itens: [{
   *         nItem, id_produto, codigo, descricao, cod_barras,
   *         unidade_medida, ncm, cfop,
   *         quantidade, valor_unitario,
   *         valor_bruto, desconto, acrescimo, valor_liquido,
   *         desconto_percentual, acrescimo_percentual,
   *         ajuste_tipo, ajuste_forma,
   *         id_produto_lote, numero_lote, data_validade, data_fabricacao,
   *         id_produto_combinacao, descricao_combinacao,
   *         pesado, peso_liquido, codigo_balanca,
   *       }],
   *       pagamentos: [{
   *         id_forma_pagamento, id_meio_pagamento, descricao,
   *         valor, parcelas, vencimento, tpag,
   *       }],
   *     }
   *
   * @param {object} payload
   * @param {string} payload.uuid_terminal
  * @param {string} payload.device_id
  * @param {number} payload.id_terminal_venda
  * @param {number} payload.id_configuracao
  * @param {number} payload.id_configuracao_nfce
   * @param {Array}  payload.nfces
   * @returns {Promise<{ resultados: Array }>}
   */
  async transmitirXmlNfce(payload = {}) {
    return this.post("offline/nfce/transmitir-xml", payload);
  },

  /**
   * Solicita o cancelamento fiscal de uma NFC-e autorizada via back_nuts (SEFAZ).
   *
   * POST /api/offline/nfce/cancelar
   *
   * @param {object} payload
   * @param {string} payload.uuid_terminal
   * @param {string} payload.uuid_venda_terminal
   * @param {string} payload.chave
   * @param {string} payload.justificativa
   * @returns {Promise<{
   *   success: boolean,
   *   protocolo_cancelamento?: string,
   *   xml_cancelamento?: string,
   *   motivo?: string,
   *   msg?: string,
   *   tipo_erro?: string,
   *   mensagem_usuario?: string,
   *   mensagem_tecnica?: string,
   *   codigo?: number,
   * }>}
   */
  async cancelarNfce(payload = {}) {
    return this.post("offline/nfce/cancelar", payload);
  },

  /**
   * Consulta no back_nuts o status atualizado das NFC-e deste terminal.
   *
   * Usado para reconciliar o estado local quando uma NFC-e é cancelada (ou tem
   * o status alterado) pelo painel web/back e o terminal ainda exibe o status
   * antigo (ex.: "Autorizada").
   *
   * POST /api/offline/nfce/status
   *
   * @param {object} payload
   * @param {string} payload.uuid_terminal
   * @param {string} [payload.device_id]
   * @param {number} [payload.id_terminal_venda]
   * @param {number} [payload.id_empresa]
   * @param {Array<{ uuid_venda_terminal?: string, chave?: string, numero?: number|string, serie?: number|string }>} [payload.nfces]
   *        Lista opcional de identificadores das NFC-e a consultar. Quando vazia,
   *        o back pode devolver as alterações recentes do terminal.
   * @returns {Promise<{ nfces?: Array, resultados?: Array, data?: Array }>}
   */
  async consultarStatusNfces(payload = {}) {
    return this.post("offline/nfce/status", payload);
  },
};

export default ApiOfflineService;
